/**
 * Routes issue subscription events to the TanStack queries they invalidate. The subscriptions
 * already refresh Relay's cache; the secondary view renders from a query nothing pushed to.
 */
import {CONNECTION_MUTATION_KEY_ROOT} from '@github-ui/optimistic-connection/pendingOperations'
import type {QueryClient} from '@github-ui/react-query'

import {issueViewerSecondaryViewQueryKey} from './fetch-issue-viewer-secondary-view'

/**
 * The part of the viewer an update touches. Every secondary-view-backed scope resolves to the same
 * query today; the indirection forces each event to declare what it affects, and means splitting
 * that query into per-section ones later changes `keysForScope` alone.
 */
export type InvalidationScope = 'sub-issues' | 'metadata' | 'header' | 'pinned-comment' | 'timeline'

/**
 * Payload fields the Relay subscriptions send; `null` when that kind of change didn't occur. This
 * layer and its two adapters below go away with the last subscription; the scope layer stays.
 */
export type IssueUpdatedPayload = {
  [K in
    | 'issueMetadataUpdated'
    | 'issueBodyUpdated'
    | 'issueTitleUpdated'
    | 'issueStateUpdated'
    | 'issueTypeUpdated'
    | 'issueReactionUpdated'
    | 'issueFieldsUpdated'
    | 'commentReactionUpdated'
    | 'commentUpdated'
    | 'pinnedCommentUpdated'
    | 'subIssuesUpdated'
    | 'subIssuesSummaryUpdated'
    | 'issueTransferStateUpdated'
    | 'parentIssueUpdated'
    | 'issueDependenciesSummaryUpdated']?: unknown
}

const SCOPE_BY_FIELD: Record<keyof IssueUpdatedPayload, InvalidationScope> = {
  subIssuesUpdated: 'sub-issues',
  // Closing a sub-issue creates no membership row, so it emits this rather than `subIssuesUpdated`,
  // and the pill is derived from the rows' `closed` flags — so the list itself must be refetched.
  subIssuesSummaryUpdated: 'sub-issues',
  issueMetadataUpdated: 'metadata',
  issueFieldsUpdated: 'metadata',
  issueTypeUpdated: 'metadata',
  issueDependenciesSummaryUpdated: 'metadata',
  parentIssueUpdated: 'metadata',
  issueTitleUpdated: 'header',
  issueStateUpdated: 'header',
  issueBodyUpdated: 'header',
  issueTransferStateUpdated: 'header',
  // Comment traffic, but the secondary query selects `pinnedIssueComment.issueComment
  // .showSpammyBadge`. Its own scope, so splitting the query later doesn't inherit a guess.
  pinnedCommentUpdated: 'pinned-comment',
  // The secondary view holds none of this, and it is the highest-volume event class on a busy
  // issue, so `timeline` resolves to no key below.
  commentUpdated: 'timeline',
  commentReactionUpdated: 'timeline',
  issueReactionUpdated: 'timeline',
}

/** The scopes a payload affects, deduped. */
export function invalidationScopesFor(payload: IssueUpdatedPayload): InvalidationScope[] {
  const scopes = new Set<InvalidationScope>()
  for (const [field, scope] of Object.entries(SCOPE_BY_FIELD) as Array<
    [keyof IssueUpdatedPayload, InvalidationScope]
  >) {
    if (payload[field] != null) scopes.add(scope)
  }
  return [...scopes]
}

type IssueCoordinates = {owner: string; repo: string; number: number}

/** Matches every mounted secondary view, whichever issue it holds. */
export const SECONDARY_VIEW_QUERY_KEY_PREFIX = ['issue-viewer', 'secondary-view'] as const

/** The query keys a scope invalidates; `timeline` maps to none. */
export function keysForScope(
  scope: InvalidationScope,
  {owner, repo, number}: IssueCoordinates,
): ReadonlyArray<readonly unknown[]> {
  if (scope === 'timeline') return []
  return [issueViewerSecondaryViewQueryKey(owner, repo, number)]
}

/**
 * The prefixes a scope invalidates across every mounted view, for a change whose holder we can't
 * identify. `keysForScope` minus the coordinates, so the per-section split stays local to this file.
 */
export function prefixesForScope(scope: InvalidationScope): ReadonlyArray<readonly unknown[]> {
  if (scope === 'timeline') return []
  return [SECONDARY_VIEW_QUERY_KEY_PREFIX]
}

/** Whether an optimistic connection operation is still in flight for `ownerId`. */
export function hasPendingConnectionOperations(queryClient: QueryClient, ownerId: string): boolean {
  return queryClient
    .getMutationCache()
    .findAll({mutationKey: [CONNECTION_MUTATION_KEY_ROOT], status: 'pending'})
    .some(mutation => mutation.options.mutationKey?.[1] === ownerId)
}

/**
 * Invalidates the queries `scopes` affect. Skipped while the overlay has writes in flight for this
 * issue: our own write echoes back as an event, and the settle re-invalidates this key anyway.
 */
export function invalidateScopes({
  queryClient,
  scopes,
  issueId,
  coordinates,
}: {
  queryClient: QueryClient
  scopes: readonly InvalidationScope[]
  issueId: string
  coordinates: IssueCoordinates
}): void {
  // Distinct scopes resolve to the same query while the view is undivided, so dedupe.
  const keys = new Map<string, readonly unknown[]>()
  for (const scope of scopes) {
    for (const queryKey of keysForScope(scope, coordinates)) {
      keys.set(JSON.stringify(queryKey), queryKey)
    }
  }
  if (keys.size === 0) return
  if (hasPendingConnectionOperations(queryClient, issueId)) return

  for (const queryKey of keys.values()) {
    void queryClient.invalidateQueries({queryKey})
  }
}

/**
 * Invalidates every mounted secondary view, for a change to an issue rendered as a row inside one.
 * Matching is by prefix because a row knows only its own id and rows nest arbitrarily deep.
 */
export function invalidateScopesForNested({
  queryClient,
  scopes,
}: {
  queryClient: QueryClient
  scopes: readonly InvalidationScope[]
}): void {
  const prefixes = new Map<string, readonly unknown[]>()
  for (const scope of scopes) {
    for (const queryKey of prefixesForScope(scope)) {
      prefixes.set(JSON.stringify(queryKey), queryKey)
    }
  }
  if (prefixes.size === 0) return

  // No pending-operation suppression here, unlike above: the settle only invalidates the mutating
  // view's own key, so dropping this prefix-wide one-shot event would strand every other view.
  for (const queryKey of prefixes.values()) {
    void queryClient.invalidateQueries({queryKey})
  }
}

/** Adapter for the Relay subscriptions, which carry a payload rather than a scope. */
export function invalidateForIssueUpdate({
  queryClient,
  payload,
  issueId,
  coordinates,
}: {
  queryClient: QueryClient
  payload: IssueUpdatedPayload
  issueId: string
  coordinates: IssueCoordinates
}): void {
  invalidateScopes({queryClient, scopes: invalidationScopesFor(payload), issueId, coordinates})
}

export function invalidateForNestedIssueUpdate({
  queryClient,
  payload,
}: {
  queryClient: QueryClient
  payload: IssueUpdatedPayload
}): void {
  invalidateScopesForNested({queryClient, scopes: invalidationScopesFor(payload)})
}
